import { createAsyncThunk, isFulfilled, isPending } from "@reduxjs/toolkit";
import axios from "axios";
import { IEdoQuestion } from "shared/model/edo-question.model";
import { IEdoQuizAttempt } from "shared/model/edo-quiz-attempt.model";
import { IPaginationResponse } from "shared/util/pagination.constants";
import {
  IEdoQuestionAnswer,
  defaultValue
} from "../../model/edo-question-answer.model";
import { cleanEntity } from "../../util/entity-utils";
import {
  EntityState,
  IQueryParamsWithFilters,
  createEntitySlice,
  serializeAxiosError
} from "../reducer.utils";

const initialState: EntityState<IEdoQuestionAnswer> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = "/question-answers";

// Actions

export const getEntities = createAsyncThunk(
  "edoQuestionAnswer/fetch_entity_list",
  async ({ page, size, sort, filters }: IQueryParamsWithFilters) => {
    const params = new URLSearchParams(filters);
    if (sort) {
      params.append("page", String(page));
      params.append("limit", String(size));
      if (typeof sort === "string") {
        params.append("sortBy", sort.replace(",", ":"));
      } else {
        for (let s of sort) {
          params.append("sortBy", s.replace(",", ":"));
        }
      }
    }
    let requestUrl;
    if (params.toString() === "") {
      requestUrl = `${apiUrl}`;
    } else {
      requestUrl = `${apiUrl}?${params.toString()}`;
    }
    return axios.get<IPaginationResponse<IEdoQuestionAnswer>>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  "edoQuestionAnswer/fetch_entity",
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<{ data: IEdoQuestionAnswer }>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  "edoQuestionAnswer/create_entity",
  async (entity: IEdoQuestionAnswer, thunkAPI) => {
    const result = await axios.post<{ data: IEdoQuestionAnswer }>(
      apiUrl,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  "edoQuestionAnswer/update_entity",
  async (entity: IEdoQuestionAnswer, thunkAPI) => {
    const result = await axios.put<{ data: IEdoQuestionAnswer }>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  "edoQuestionAnswer/partial_update_entity",
  async (entity: IEdoQuestionAnswer, thunkAPI) => {
    const result = await axios.patch<{ data: IEdoQuestionAnswer }>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  "edoQuestionAnswer/delete_entity",
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IEdoQuestionAnswer>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createScore = createAsyncThunk(
  "edoQuestionAnswer/create_score",
  async (
    data: {
      score: number;
      question: IEdoQuestion;
      quizAttempt: IEdoQuizAttempt;
    },
    thunkAPI
  ) => {
    const result = await axios.post<{ data: IEdoQuestionAnswer }>(
      `${apiUrl}/score`,
      {
        score: data.score,
        question: data.question,
        quizAttempt: data.quizAttempt,
      }
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateScore = createAsyncThunk(
  "edoQuestionAnswer/update_score",
  async (data: { id: number; score: number }, thunkAPI) => {
    const result = await axios.put<{ data: IEdoQuestionAnswer }>(
      `${apiUrl}/${data.id}/score`,
      {
        score: data.score,
      }
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const EdoQuestionAnswerSlice = createEntitySlice({
  name: "edoQuestionAnswer",
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data.data;
      })
      .addCase(deleteEntity.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const {
          data: {
            data,
            meta: { totalItems },
          },
          headers,
        } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems,
        };
      })
      .addMatcher(
        isFulfilled(
          createEntity,
          updateEntity,
          partialUpdateEntity,
          createScore
        ),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data.data;
        }
      )
      .addMatcher(isFulfilled(updateScore), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getEntities, getEntity), (state) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          createEntity,
          updateEntity,
          partialUpdateEntity,
          deleteEntity,
          createScore,
          updateScore
        ),
        (state) => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = EdoQuestionAnswerSlice.actions;

// Reducer
export default EdoQuestionAnswerSlice.reducer;
